import React, { useEffect, useReducer, useState } from "react";
import styled from "styled-components";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import HeadingOne from "../../components/HeadingOne";
import StateInput from "../../components/StateInput";
import StyledButton from "../../components/StyledButton";
import colors from "../../constants/colors";

const SubmitWithdrawal = gql`
  mutation submitWithdrawal(
    $name: String!
    $orderNumber: String!
    $email: String!
    $items: String
    $reason: String
  ) {
    submitWithdrawal(
      input: {
        clientMutationId: "SubmitWithdrawal"
        name: $name
        orderNumber: $orderNumber
        email: $email
        items: $items
        reason: $reason
      }
    ) {
      success
    }
  }
`;

const Widerruf = () => {
  useEffect(() => {
    document.title = "Vertrag widerrufen";
  }, []);

  const [form, setForm] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      name: "",
      orderNumber: "",
      email: "",
      items: "",
      reason: "",
    }
  );

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [submitWithdrawal, { loading }] = useMutation(SubmitWithdrawal);

  const handleSubmit = () => {
    const name = form.name.trim();
    const orderNumber = form.orderNumber.trim();
    const email = form.email.trim();

    const newErrors = {};
    if (!name) newErrors.name = "Bitte gib deinen Namen ein.";
    if (!orderNumber)
      newErrors.orderNumber = "Bitte gib die Bestell- oder Buchungsnummer ein.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email))
      newErrors.email = "Ungültige E-Mail-Adresse.";

    setErrors(newErrors);
    setSubmitError(false);
    if (Object.keys(newErrors).length > 0) return;

    submitWithdrawal({
      variables: {
        name,
        orderNumber,
        email,
        items: form.items.trim(),
        reason: form.reason.trim(),
      },
    })
      .then(() => setSubmitted(true))
      .catch(() =>
        setSubmitError(
          "Der Widerruf konnte nicht übermittelt werden. Bitte versuche es erneut oder schreibe uns an info@gutgereift.at."
        )
      );
  };

  if (submitted) {
    return (
      <Wrapper>
        <HeadingOne title="Vertrag widerrufen" />
        <Paragraph>
          <HeadingFour>✓ Widerruf eingegangen</HeadingFour>
          <Text>
            Eine Eingangsbestätigung wurde an die angegebene E-Mail-Adresse
            gesendet.
          </Text>
        </Paragraph>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <HeadingOne title="Vertrag widerrufen" />
      <Paragraph>
        <Text>
          Online geschlossene Verträge können innerhalb von 14 Tagen widerrufen
          werden. Bitte fülle das folgende Formular aus. Eine
          Eingangsbestätigung wird dir umgehend per E-Mail zugesendet.
        </Text>
      </Paragraph>
      <Paragraph>
        <InputWrapper>
          <StateInput
            forwardedRef={form.name}
            type={"text"}
            placeholder={"Name *"}
            error={errors.name}
            maxLength="100"
            onChange={(e) => setForm({ name: e.target.value })}
          ></StateInput>
        </InputWrapper>
        <InputWrapper>
          <StateInput
            forwardedRef={form.orderNumber}
            type={"text"}
            placeholder={"Bestell- oder Buchungsnummer *"}
            error={errors.orderNumber}
            maxLength="100"
            onChange={(e) => setForm({ orderNumber: e.target.value })}
          ></StateInput>
          <HelpText>Dies ist in der Bestellbestätigung zu finden.</HelpText>
        </InputWrapper>
        <InputWrapper>
          <StateInput
            forwardedRef={form.email}
            type={"email"}
            placeholder={"E-Mail-Adresse *"}
            error={errors.email}
            maxLength="100"
            onChange={(e) => setForm({ email: e.target.value })}
          ></StateInput>
          <HelpText>
            Die Eingangsbestätigung wird an diese Adresse gesendet.
          </HelpText>
        </InputWrapper>
        <InputWrapper>
          <StateInput
            forwardedRef={form.items}
            type={"textarea"}
            placeholder={"Welche Artikel sollen widerrufen werden?"}
            maxLength="2000"
            onChange={(e) => setForm({ items: e.target.value })}
          ></StateInput>
          <HelpText>
            Leer lassen, um den gesamten Vertrag zu widerrufen. Um nur einen
            Teil der Bestellung zu widerrufen, die Artikel hier angeben.
          </HelpText>
        </InputWrapper>
        <InputWrapper>
          <StateInput
            forwardedRef={form.reason}
            type={"textarea"}
            placeholder={"Grund des Widerrufs (optional)"}
            maxLength="2000"
            onChange={(e) => setForm({ reason: e.target.value })}
          ></StateInput>
        </InputWrapper>
      </Paragraph>
      <Paragraph>
        <Text>
          Ein Klick auf die Schaltfläche unten erklärt verbindlich den Widerruf
          des oben angegebenen Vertrags. Dieser Schritt kann nicht rückgängig
          gemacht werden.
        </Text>
        <HelpText>Alle mit * markierten Felder sind Pflichtfelder.</HelpText>
        <ButtonWrapper>
          <StyledButton
            scale={"ORDER"}
            title={"Vertrag widerrufen"}
            color={colors.YellowBrigthButton}
            fontSize={"20px"}
            onClick={handleSubmit}
            loading={loading}
            error={submitError}
          />
        </ButtonWrapper>
      </Paragraph>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-left: 10vw;
  margin-top: 10vh;
  margin-bottom: 10vh;
`;

const Paragraph = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  width: 50vw;
  @media (max-width: 800px) {
    width: 80vw;
  }
`;

const InputWrapper = styled.div`
  width: 80%;
  @media (max-width: 800px) {
    width: 100%;
  }
`;

const Text = styled.div`
  font-family: "Montserrat";
  font-weight: 300;
  font-size: 16px;
  letter-spacing: 0.4px;
  color: #333333;
  margin-top: 0;
  text-align: left;
`;

const HelpText = styled.p`
  font-family: "Montserrat";
  font-weight: 400;
  font-size: 13px;
  letter-spacing: 0.2px;
  color: ${colors.SolidGreyText};
  margin: 4px 0 10px 0;
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;
`;

const HeadingFour = styled.h4`
  color: #023682;
  font-family: "Sorts Mill Goudy";
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 400;
`;

export default Widerruf;
